// src/components/Home/CaseRowShieldToggle.tsx
// ──────────────────────────────────────────────────────────────
// Minimal shield arm/disarm toggle for case list rows.
// Arms with 2hr default duration on tap. Shows active state
// with pulsing indicator. Tap again to disarm.
// ──────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback } from "react";
import { useUser } from "@clerk/clerk-react";
import { supabase } from "../../lib/supabase";

interface CaseRowShieldToggleProps {
  caseId: string;
}

type ShieldStatus = "idle" | "armed" | "active" | "loading";

function ShieldIcon({ armed, className }: { armed: boolean; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 28" fill="none">
      {armed ? (
        <>
          <path
            d="M12 0 L24 3.8 L24 14.5 C24 21.1 18 25.5 12 28 C6 25.5 0 21.1 0 14.5 L0 3.8 Z"
            fill="currentColor"
          />
          <path
            d="M12 1.5 L23 4.9 L23 14.2 C23 20.4 17.5 24.4 12 26.8 C6.5 24.4 1 20.4 1 14.2 L1 4.9 Z"
            fill="white"
            opacity="0.2"
          />
          <path
            d="M8.3 13.8 L10.8 16.6 L16.3 10.4"
            stroke="white"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </>
      ) : (
        <path
          d="M12 1 L23 4.5 L23 14.3 C23 20.5 17.5 24.8 12 27.2 C6.5 24.8 1 20.5 1 14.3 L1 4.5 Z"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

export function CaseRowShieldToggle({ caseId }: CaseRowShieldToggleProps) {
  const { user } = useUser();
  const [status, setStatus] = useState<ShieldStatus>("idle");
  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  const userId = user?.id;

  // Check for existing armed shield on mount
  useEffect(() => {
    if (!userId || !caseId) return;

    const check = async () => {
      const { data: shield } = await supabase
        .from("call_shields")
        .select("id, status, expires_at")
        .eq("case_id", caseId)
        .eq("user_id", userId)
        .in("status", ["armed", "active"])
        .order("armed_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (shield) {
        // Check if actually expired
        if (new Date(shield.expires_at) > new Date()) {
          setStatus(shield.status as "armed" | "active");
          setExpiresAt(shield.expires_at);
        }
      }
    };

    check();
  }, [userId, caseId]);

  // Arm with default 2hr duration
  const arm = useCallback(async () => {
    if (!userId || !caseId) return;
    setStatus("loading");

    try {
      const { data: profile } = await supabase
        .from("physician_profiles")
        .select("physician_name, npi_number, practice_name, phone_number")
        .eq("user_id", userId)
        .maybeSingle();

      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/call-shield-arm`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: "arm",
            case_id: caseId,
            user_id: userId,
            display_name: profile?.practice_name
              ? `${profile.practice_name}`
              : profile?.physician_name
                ? `${profile.physician_name}'s office`
                : "Doctor's office",
            physician_name: profile?.physician_name || undefined,
            physician_npi: profile?.npi_number || undefined,
            practice_name: profile?.practice_name || undefined,
            physician_phone: profile?.phone_number || undefined,
            window_hours: 2,
          }),
        }
      );

      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error);

      setStatus("armed");
      setExpiresAt(data.expires_at);
    } catch (e: any) {
      console.error("[CaseRowShield] Arm failed:", e.message);
      setStatus("idle");
    }
  }, [userId, caseId]);

  // Disarm
  const disarm = useCallback(async () => {
    if (!userId || !caseId) return;
    setStatus("loading");

    try {
      await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/call-shield-arm`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: "disarm",
            case_id: caseId,
            user_id: userId,
          }),
        }
      );

      setStatus("idle");
      setExpiresAt(null);
    } catch {
      setStatus("armed");
    }
  }, [userId, caseId]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (status === "loading") return;
    if (status === "armed" || status === "active") {
      disarm();
    } else {
      arm();
    }
  };

  const isArmed = status === "armed" || status === "active";
  const isLoading = status === "loading";

  // Time remaining label for tooltip
  let tooltip = "Arm Call Shield (2hr)";
  if (isArmed && expiresAt) {
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff > 0) {
      const mins = Math.floor(diff / 60000);
      const hrs = Math.floor(mins / 60);
      const remMins = mins % 60;
      tooltip = hrs > 0
        ? `Shield active – ${hrs}h ${remMins}m remaining. Click to disarm.`
        : `Shield active – ${remMins}m remaining. Click to disarm.`;
    }
  }

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      title={tooltip}
      className={`
        relative flex-shrink-0 p-1.5 rounded-lg transition-all duration-200
        focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:rounded-lg
        ${isArmed
          ? "text-purple-600 bg-purple-50 hover:bg-purple-100 border border-purple-200"
          : "text-slate-300 hover:text-purple-500 hover:bg-purple-50 border border-transparent"
        }
        ${isLoading ? "opacity-50 cursor-wait" : "cursor-pointer"}
      `}
      aria-label={isArmed ? "Disarm Call Shield" : "Arm Call Shield"}
    >
      {isLoading ? (
        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity={0.25} />
          <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      ) : (
        <ShieldIcon armed={isArmed} className="w-4 h-4" />
      )}

      {/* Pulsing dot when armed */}
      {isArmed && !isLoading && (
        <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
        </span>
      )}
    </button>
  );
}